import { useState, useEffect, useMemo, type ReactNode } from 'react'
import {
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  Receipt,
  Scale,
  UserCheck,
} from 'lucide-react'
import detailStyles from '@/features/reimbursements/styles/reimbursementDetail.module.css'
import {
  deleteEntityActionButtonClass,
  primaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { openDefaultPaymentLogDetail } from '@/lib/utils/reportsNavigation'
import { CLAIM_HISTORY_STATUS, PAYMENT_STATUSES } from '../../constants/residentConstants'
import type { AdjustmentData, PaymentReviewFormRow} from '../../types/resident'
import type { PaymentReviewLogSectionProps, ReviewApprovalField, ReviewApprovalValue } from '../../types'

/**
 * Formats Adjustment Snapshot dates as MM-DD-YYYY.
 * Handles ISO timestamps (`2026-11-30T06:30:00.000Z`), `yyyy-MM-dd`, and
 * `MM/DD/YYYY` / `MM-DD-YYYY` without UTC→local day shifts.
 */
function formatAdjustmentDate(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-'
  const raw = String(value).trim()
  if (!raw) return '-'

  // Calendar date from ISO / yyyy-MM-dd — use the date prefix, not local timezone
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (ymd) {
    return `${ymd[2]}-${ymd[3]}-${ymd[1]}`
  }

  // Already MM-DD-YYYY or MM/DD/YYYY
  const mdy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/)
  if (mdy) {
    return `${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}-${mdy[3]}`
  }

  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return raw
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${mm}-${dd}-${yyyy}`
}

const editPaymentButtonClass = cn(primaryEntityActionButtonClass, 'h-9 min-w-0 whitespace-nowrap px-3 text-xs')
const deletePaymentButtonClass = cn(deleteEntityActionButtonClass, 'h-9 min-w-0 whitespace-nowrap px-3 text-xs')

function isCompletedPaymentStatus(status: unknown): boolean {
  const raw = String(status || '').trim()
  if (!raw) return false
  if (['Completed', 'COMPLETED', 'Paid'].includes(raw)) return true
  const label = PAYMENT_STATUSES[raw as keyof typeof PAYMENT_STATUSES]
  return label === 'Paid'
}

function formatDate(value: unknown): string {
  if (!value || (typeof value !== 'string' && typeof value !== 'number')) return ''
  const date = new Date(typeof value === 'number' ? value * 1000 : value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })
}

/** Angular payment review "Payment Date": `created_at | date:'MM/dd/yyyy'`. */
function formatPaymentDateOnly(value: unknown): string {
  if (!value || (typeof value !== 'string' && typeof value !== 'number')) return ''
  const date =
    typeof value === 'number'
      ? new Date(value > 1e12 ? value : value * 1000)
      : !Number.isNaN(Number(value)) && String(value).trim().length <= 13
        ? new Date(Number(value) > 1e12 ? Number(value) : Number(value) * 1000)
        : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const yyyy = date.getFullYear()
  return `${mm}/${dd}/${yyyy}`
}

function formatAmount(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-'
  const num = Number(String(value).replace(/[^0-9.-]/g, ''))
  if (!Number.isFinite(num)) return '-'
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

/**
 * Mirrors Angular's payment_review_log array UI on the Calculation tab.
 * Each row supports the L1 / L2 / L3 approval workflow, expand-on-click for
 * details, edit/delete actions, and adjustment_data display.
 */
export function PaymentReviewLogSection({
  residentDetail,
  isLibertyRole,
  canL1Approve,
  canL2Approve,
  canL3Approve,
  canDeletePayment,
  canEditPayment,
  processing,
  onSubmitPaymentApproval,
  onSubmitClaimApproval,
  onEditPayment,
  onDeletePayment,
  onRejectClaim,
}: PaymentReviewLogSectionProps) {
  const rows = residentDetail?.payment_review_log || []
  const [expandedId, setExpandedId] = useState<string | number | null>(() => {
    const firstActive = rows.find(
      (row) => !['L1 Recalculated', 'L2 Recalculated', 'Approved'].includes(String(row.review_decision || '')),
    )
    return firstActive?.review_log_id || firstActive?.payment_log_id || null
  })

  // Match Angular: only render Payment Review Log when entries exist.
  if (!rows.length) return null

  return (
    <section className={detailStyles.section} data-testid="resident-payment-review-log">
      <h2 className={detailStyles.sectionTitle}>
        <span className={detailStyles.sectionTitleIcon} aria-hidden="true">
          <Receipt className="h-4 w-4" />
        </span>
        Payment Review Log
      </h2>
      <div className="space-y-3">
      {rows.map((row, index) => {
        const id = row.payment_log_id || row.review_log_id || index
        const expanded = expandedId === id
        const isPaymentRow = !!row.payment_log_id
        const isInvoice = row.payment_type === 'DEFAULT_INVOICE'
        const isCompleted = isCompletedPaymentStatus(row.payment_status)
        const isLockedByDecision = ['L1 Recalculated', 'L2 Recalculated'].includes(
          String(row.review_decision || ''),
        )
        const statusLabel = isPaymentRow
          ? PAYMENT_STATUSES[row.payment_status as keyof typeof PAYMENT_STATUSES] ||
            String(row.payment_status || '')
          : CLAIM_HISTORY_STATUS[String(row.claim_status || '')] || String(row.claim_status || '')
        const canShowActions = isPaymentRow
          ? isLibertyRole && !isInvoice && (canEditPayment || canDeletePayment)
          : canDeletePayment && !row.rejected_at
        const rowTitle = isPaymentRow
          ? isInvoice
            ? 'Recovery'
            : 'Pay Reimbursement'
          : CLAIM_HISTORY_STATUS[String(row.claim_status || '')] ||
            String(row.claim_status || 'Claim Review')

        return (
          <div
            key={String(id)}
            className="rounded-xl border border-slate-200/90 bg-slate-50/40 p-4 shadow-2xs transition-colors hover:border-slate-300"
            data-testid={`resident-payment-review-log-row-${id}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-2.5">
                <span className="text-sm font-semibold text-slate-800">{rowTitle}</span>
                {statusLabel ? <StatusBadge>{statusLabel}</StatusBadge> : null}
                {isPaymentRow && row.payment_amount != null && row.payment_amount !== '' ? (
                  <span className="text-sm font-medium text-slate-600">
                    {formatAmount(row.payment_amount)}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setExpandedId(expanded ? null : id)}
                data-testid={`resident-payment-review-log-row-${id}-toggle`}
                className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-[#1597ff] transition hover:text-[#0d7acc] hover:underline"
              >
                {expanded ? 'Collapse' : 'Expand'}
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            </div>

            {expanded ? (
              <div
                className="mt-4 space-y-4 border-t border-slate-100 pt-4"
                data-testid={`resident-payment-review-log-row-${id}-expanded`}
              >
                <SubCard icon={<Receipt className="h-3.5 w-3.5" />} title="Payment Overview">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Field
                      label={isInvoice ? 'Payment Type' : 'Recommendation'}
                      value={
                        isInvoice
                          ? 'Recovery'
                          : isPaymentRow
                            ? 'Pay Reimbursement'
                            : CLAIM_HISTORY_STATUS[String(row.claim_status || '')] || row.claim_status
                      }
                    />
                    {isLibertyRole && !isInvoice ? (
                      <FieldBadge label="Review Decision" value={row.review_decision} />
                    ) : null}
                    {isPaymentRow ? (
                      <>
                        <Field label="Payment" value={formatAmount(row.payment_amount)} />
                        <Field label="Payment Date" value={formatPaymentDateOnly(row.created_at)} />
                      </>
                    ) : (
                      <Field
                        label="Created By"
                        value={`${row.created_by_name || ''} ${row.created_at ? `- ${formatDate(row.created_at)}` : ''}`}
                      />
                    )}
                  </div>
                  {isPaymentRow ? (
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field
                        label={isInvoice ? 'Transaction Type' : 'Check / ACH Transaction'}
                        value={isInvoice ? 'Invoice' : row.payment_source}
                      />
                      <Field
                        label={isInvoice ? 'Payment/Invoice ID' : 'Check Number'}
                        value={
                          row.check_number && row.check_number !== '0000' ? row.check_number : '-'
                        }
                      />
                    </div>
                  ) : null}
                </SubCard>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr] lg:items-stretch">
                  <SubCard icon={<UserCheck className="h-3.5 w-3.5" />} title="Approval Review" className="h-full">
                    <div className="space-y-4">
                      {isPaymentRow ? (
                        <div className="block text-sm font-medium text-slate-700">
                          <span className="mb-1 block text-xs font-medium text-slate-600">
                            Log Number
                          </span>
                          {isLibertyRole && row.payment_log_id ? (
                            <button
                              type="button"
                              onClick={() => {
                                openDefaultPaymentLogDetail(
                                  row.payment_log_id as string | number,
                                )
                              }}
                              data-testid={`resident-payment-review-log-row-${id}-log-number`}
                              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-left text-sm font-semibold text-[#1597ff] shadow-2xs transition hover:underline focus:outline-none focus:ring-2 focus:ring-blue-100"
                            >
                              {row.payment_log_id}
                            </button>
                          ) : (
                            <input
                              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 shadow-2xs focus:outline-none"
                              value={row.payment_log_id ? String(row.payment_log_id) : '-'}
                              readOnly
                            />
                          )}
                        </div>
                      ) : null}

                      {isLibertyRole && isCompleted ? <ApprovalCompletedSummary row={row} /> : null}

                      {isLibertyRole && !isCompleted && !isLockedByDecision ? (
                        <ApprovalControls
                          row={row}
                          isPaymentRow={isPaymentRow}
                          isInvoice={isInvoice}
                          canL1Approve={canL1Approve}
                          canL2Approve={canL2Approve}
                          canL3Approve={canL3Approve}
                          l2Review={Boolean(residentDetail?.resident_default?.l2_review)}
                          processing={processing}
                          onSubmitPaymentApproval={onSubmitPaymentApproval}
                          onSubmitClaimApproval={onSubmitClaimApproval}
                        />
                      ) : null}

                      <ReviewRemarks row={row} />
                    </div>
                  </SubCard>

                  <SubCard icon={<CircleDollarSign className="h-3.5 w-3.5" />} title="Payment Action" className="h-full">
                    <div className="flex h-full flex-col space-y-4">
                      {isPaymentRow ? (
                        <Field
                          label="Payment Status"
                          value={
                            PAYMENT_STATUSES[row.payment_status as keyof typeof PAYMENT_STATUSES] ||
                            row.payment_status ||
                            '-'
                          }
                        />
                      ) : null}

                      {canShowActions ? (
                        <div>
                          <span className="mb-2 block text-sm font-medium text-slate-700">Actions</span>
                          <div className="flex flex-wrap items-center gap-2">
                            {isPaymentRow ? (
                              <>
                                {canEditPayment ? (
                                  <button
                                    type="button"
                                    disabled={processing || isLockedByDecision}
                                    onClick={() => onEditPayment?.(row)}
                                    data-testid={`resident-payment-review-log-row-${id}-edit`}
                                    className={editPaymentButtonClass}
                                  >
                                    Edit Payment
                                  </button>
                                ) : null}
                                {canDeletePayment ? (
                                  <button
                                    type="button"
                                    disabled={processing || isLockedByDecision}
                                    onClick={() => onDeletePayment?.(row)}
                                    data-testid={`resident-payment-review-log-row-${id}-delete`}
                                    className={deletePaymentButtonClass}
                                  >
                                    Delete
                                  </button>
                                ) : null}
                              </>
                            ) : (
                              <button
                                type="button"
                                disabled={processing}
                                onClick={() => onRejectClaim?.(row)}
                                data-testid={`resident-payment-review-log-row-${id}-reject`}
                                className="rounded-lg border border-amber-200 px-3 py-1 text-xs font-semibold text-amber-700 disabled:opacity-50"
                              >
                                Reject
                              </button>
                            )}
                          </div>
                        </div>
                      ) : null}
                      {!isPaymentRow && row.rejected_at ? (
                        <span className="text-xs text-slate-500">Rejected</span>
                      ) : null}

                      <p className="mt-auto text-xs leading-snug text-slate-500">
                        Review the adjustment details before approving the payment.
                      </p>
                    </div>
                  </SubCard>
                </div>

                {row.adjustment_data ? (
                  <AdjustmentSnapshot adjustment={normalizeAdjustmentData(row.adjustment_data)} />
                ) : null}
              </div>
            ) : null}
          </div>
        )
      })}
      </div>
    </section>
  )
}

function StatusBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
      {children}
    </span>
  )
}

function SubCard({
  icon,
  title,
  children,
  className,
}: {
  icon: ReactNode
  title: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xs', className)}>
      <div className="flex items-center gap-2.5 border-b border-[#dbe7f3] bg-[#eef6fc] px-4 py-2.5">
        <span
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-[#1597ff] ring-1 ring-[#cfe6f8]"
          aria-hidden="true"
        >
          {icon}
        </span>
        <h3 className="m-0 text-xs font-bold uppercase tracking-wider text-slate-800">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  )
}

function Field({
  label,
  value,
  multiline = false,
}: {
  label: string
  value?: React.ReactNode
  multiline?: boolean
}) {
  const display =
    value === null || value === undefined || value === ''
      ? '-'
      : String(value)

  return (
    <label className="block text-sm font-medium text-slate-700">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      {multiline ? (
        <textarea
          className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-2xs focus:outline-none"
          value={display}
          readOnly
          rows={3}
        />
      ) : (
        <input
          className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 shadow-2xs focus:outline-none"
          value={display}
          readOnly
        />
      )}
    </label>
  )
}

/** Same label/value pairing as {@link Field}, with the value rendered as an amber status pill. */
function FieldBadge({ label, value }: { label: string; value?: React.ReactNode }) {
  const display = value === null || value === undefined || value === '' ? '' : String(value)
  return (
    <div className="block text-sm font-medium text-slate-700">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      {display ? <StatusBadge>{display}</StatusBadge> : <span className="text-slate-400">-</span>}
    </div>
  )
}

function ApprovalCompletedSummary({ row }: { row: PaymentReviewFormRow }) {
  return (
    <div
      className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2"
      data-testid="resident-payment-review-log-completed-summary"
    >
      <Field
        label="Liberty 2nd Level Approval"
        value={`${row.liberty_authorization_user_name || row.liberty_authorization_approval_by || ''} ${
          row.liberty_authorization_approval_date ? `- ${formatDate(row.liberty_authorization_approval_date)}` : ''
        }`}
      />
      <Field
        label="Liberty 3rd Level Approval"
        value={`${row.insurance_user_name || row.insurance_review_approval_by || ''} ${
          row.insurance_review_approval_date ? `- ${formatDate(row.insurance_review_approval_date)}` : ''
        }`}
      />
      <Field
        label="Created By"
        value={`${row.added_by || ''} ${
          row.payment_created_date ? `- ${formatDate(row.payment_created_date)}` : ''
        }`}
      />
    </div>
  )
}

function ApprovalControls({
  row,
  isPaymentRow,
  isInvoice,
  canL1Approve,
  canL2Approve,
  canL3Approve,
  l2Review,
  processing,
  onSubmitPaymentApproval,
  onSubmitClaimApproval,
}: {
  row: PaymentReviewFormRow
  isPaymentRow: boolean
  isInvoice: boolean
  canL1Approve: boolean
  canL2Approve: boolean
  canL3Approve: boolean
  l2Review: boolean
  processing: boolean
  onSubmitPaymentApproval: PaymentReviewLogSectionProps['onSubmitPaymentApproval']
  onSubmitClaimApproval: PaymentReviewLogSectionProps['onSubmitClaimApproval']
}) {
  if (isInvoice) return null

  // L1 dropdown only when row was returned to L1 (liberty_approved/liberty_authorization_approval === '0')
  // or has an existing l1_approved value.
  const showL1 =
    isPaymentRow
      ? row.liberty_approved === '0' || !!row.l1_approved
      : row.liberty_authorization_approval === '0' || !!row.l1_approved

  // L3 dropdown is shown when L2 escalated (value '2' or '4') or when an
  // insurance review approval has been recorded.
  // Angular loadResidentDetail: copy liberty_authorization_approval → liberty_approved when missing.
  const liberty2Value = isPaymentRow
    ? row.liberty_approved || row.liberty_authorization_approval
    : row.liberty_authorization_approval
  const showL3 =
    ['2', '4'].includes(String(liberty2Value || '')) || !!row.insurance_review_approval

  const submit = async (
    field: ReviewApprovalField,
    value: ReviewApprovalValue,
  ) => {
    if (isPaymentRow && row.payment_log_id) {
      await onSubmitPaymentApproval(row.payment_log_id, field, value)
    } else if (!isPaymentRow && row.review_log_id) {
      await onSubmitClaimApproval(row.review_log_id, field, value)
    }
  }

  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      data-testid="resident-payment-review-log-approval-controls"
    >
      {showL1 ? (
        <ApprovalSelect
          label="Liberty 1st Level Returned Approval"
          value={String(row.l1_approved ?? '')}
          disabled={!canL1Approve || !!row.l1_approved || processing}
          existingByline={
            row.l1_approved_username
              ? `${row.l1_approved_username} - ${formatDate(row.l1_approved_date)}`
              : ''
          }
          options={[
            { value: '', label: '', disabled: true },
            { value: '2', label: 'Resubmit to L2' },
            { value: '1', label: 'Recalculate' },
          ]}
          onChange={(value) => submit('l1_approved', value)}
        />
      ) : null}

      <ApprovalSelect
        label="Liberty 2nd Level Approval"
        value={String(liberty2Value ?? '')}
        disabled={
          !canL2Approve ||
          (isPaymentRow ? !!row.liberty_approved : !!row.liberty_authorization_approval) ||
          processing
        }
        existingByline={
          row.liberty_authorization_user_name || row.liberty_authorization_approval_by
            ? `${row.liberty_authorization_user_name || row.liberty_authorization_approval_by} - ${formatDate(row.liberty_authorization_approval_date)}`
            : ''
        }
        options={[
          { value: '', label: '', disabled: true },
          { value: '1', label: 'Approve' },
          ...(row.insurance_review_approval !== '0' || String(liberty2Value) === '2'
            ? ([{ value: '2', label: 'Escalate to L3 Review' }] as const)
            : []),
          // Angular: Return to L1 when !l2_review — keep option if it is the saved value so it displays.
          ...(!l2Review || String(liberty2Value) === '0'
            ? ([{ value: '0', label: 'Return to L1' }] as const)
            : []),
          ...(row.insurance_review_approval === '0' ||
          row.review_decision === 'Resubmitted to L3' ||
          row.liberty_approved === '4' ||
          row.liberty_authorization_approval === '4' ||
          String(liberty2Value) === '4'
            ? ([{ value: '4', label: 'Resubmit to L3' }] as const)
            : []),
          { value: '3', label: 'Recalculate' },
        ]}
        onChange={(value) =>
          submit(isPaymentRow ? 'liberty_approved' : 'liberty_authorization_approval', value)
        }
      />

      {showL3 ? (
        <ApprovalSelect
          label="Liberty 3rd Level Approval"
          value={String(row.insurance_review_approval ?? '')}
          disabled={!canL3Approve || !!row.insurance_review_approval || processing}
          existingByline={
            row.insurance_user_name || row.insurance_review_approval_by
              ? `${row.insurance_user_name || row.insurance_review_approval_by} - ${formatDate(row.insurance_review_approval_date)}`
              : ''
          }
          options={[
            { value: '', label: '', disabled: true },
            { value: '1', label: 'Approve' },
            { value: '0', label: 'Return to L2' },
          ]}
          onChange={(value) => submit('insurance_review_approval', value)}
        />
      ) : null}
    </div>
  )
}

function ApprovalSelect({
  label,
  value = '',
  disabled,
  existingByline,
  options,
  onChange,
}: {
  label: string
  /** Saved approval code from the row — blank when unset (Angular formControl patch). */
  value?: string
  disabled?: boolean
  existingByline?: string
  options: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>
  onChange: (value: ReviewApprovalValue) => void
}) {
  const [localValue, setLocalValue] = useState(String(value ?? ''))

  useEffect(() => {
    setLocalValue(String(value ?? ''))
  }, [value])

  const selected = localValue

  const allOptions = useMemo(() => {
    const hasSelected = options.some((option) => option.value === selected)
    if (selected && !hasSelected) {
      const fallbackLabelMap: Record<string, string> = {
        '1': 'Approve',
        '2': 'Escalate to L3 Review',
        '0': 'Return to L1',
        '3': 'Recalculate',
        '4': 'Resubmit to L3',
      }
      return [
        ...options,
        { value: selected, label: fallbackLabelMap[selected] ?? selected },
      ]
    }
    return options
  }, [options, selected])

  return (
    <label className="text-sm font-medium text-slate-700">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      <select
        disabled={disabled}
        value={selected}
        onChange={(event) => {
          const next = event.target.value as ReviewApprovalValue
          if (!next) return
          setLocalValue(next)
          onChange(next)
        }}
        className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none transition focus:border-[#1597ff] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100/70 disabled:opacity-60 shadow-2xs"
      >
        {allOptions.map((option) => (
          <option key={option.value || 'none'} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      {existingByline ? <p className="mt-1 text-xs text-slate-500 font-normal">{existingByline}</p> : null}
    </label>
  )
}

function ReviewRemarks({ row }: { row: PaymentReviewFormRow }) {
  const blocks: Array<{ label: string; value?: string }> = []
  if (
    (['L2 Approval Needed', 'Resubmitted to L2'].includes(String(row.review_decision || '')) ||
      row.l1_approved === '1') &&
    row.review_remark
  ) {
    blocks.push({ label: 'L1 Review Remarks', value: String(row.review_remark) })
  }
  if (
    (['Escalated to L3 Review', 'Resubmitted to L3', 'Returned to L1'].includes(
      String(row.review_decision || ''),
    ) ||
      ['1', '3'].includes(String(row.liberty_approved || ''))) &&
    row.liberty_authorization_approval_remark
  ) {
    blocks.push({
      label: 'L2 Review Remarks',
      value: String(row.liberty_authorization_approval_remark),
    })
  }
  if (
    (['Returned to L2'].includes(String(row.review_decision || '')) ||
      row.insurance_review_approval === '1') &&
    row.insurance_review_approval_remark
  ) {
    blocks.push({
      label: 'L3 Review Remarks',
      value: String(row.insurance_review_approval_remark),
    })
  }
  if (!blocks.length) return null
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      data-testid="resident-payment-review-log-remarks"
    >
      {blocks.map((block) => (
        <Field key={block.label} label={block.label} value={block.value} multiline />
      ))}
    </div>
  )
}

/** API sometimes returns adjustment_data as a JSON string. */
function normalizeAdjustmentData(value: unknown): AdjustmentData {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as AdjustmentData
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as AdjustmentData
      }
    } catch {
      // fall through
    }
  }
  return {}
}

function AdjustmentSnapshot({ adjustment }: { adjustment: AdjustmentData }) {
  return (
    <SubCard
      icon={<BarChart3 className="h-3.5 w-3.5" />}
      title="Adjustment Snapshot"
      className="[&>div:last-child]:p-0"
    >
      <div
        className="grid grid-cols-1 divide-y divide-slate-100 lg:grid-cols-3 lg:divide-x lg:divide-y-0"
        data-testid="resident-payment-review-log-adjustment-data"
      >
        <div className="p-4">
          <SubCardHeading icon={<Calendar className="h-3.5 w-3.5" />}>Dates &amp; Period</SubCardHeading>
          <div className="space-y-2.5">
            <StatRow
              label="Reimbursement Request Date"
              value={formatAdjustmentDate(adjustment.rental_status_date)}
            />
            <StatRow label="Adjusted Notice" value={formatAdjustmentDate(adjustment.adjusted_notice)} />
            <StatRow
              label="Reimbursement End Date"
              value={formatAdjustmentDate(adjustment.adjust_max_movein_date)}
            />
            <StatRow
              label="New Resident Move-In Date"
              value={formatAdjustmentDate(adjustment.movein_date)}
            />
            <StatRow label="Reimbursement Period" value={asNumberOrDash(adjustment.notice_month_diff)} />
            <StatRow label="Timely" value={adjustment.timely || 'YES'} />
            <StatRow label="Adjusted Reason" value={adjustment.adjusted_reason} />
          </div>
        </div>

        <div className="p-4">
          <SubCardHeading icon={<CircleDollarSign className="h-3.5 w-3.5" />}>Reimbursement</SubCardHeading>
          <div className="space-y-2.5">
            <StatRow label="Reimbursement Rate" value={formatAmount(adjustment.rent_amount)} />
            <StatRow label="Daily Rate (30d)" value={formatAmount(adjustment.daily_rent)} />
            <div className="mt-3 rounded-lg bg-sky-50 px-3 py-2.5">
              <p className="m-0 text-xs font-semibold text-[#1597ff]">Net Reimbursement Payment</p>
              <p className="m-0 mt-0.5 text-lg font-bold text-[#1597ff]">{formatAmount(adjustment.net_due)}</p>
            </div>
          </div>
        </div>

        <div className="p-4">
          <SubCardHeading icon={<Scale className="h-3.5 w-3.5" />}>Loss Adjustments</SubCardHeading>
          <div className="space-y-2.5">
            <StatRow label="Lost Rent Revenue" value={formatAmount(adjustment.loss_rent)} />
            <StatRow label="Unused Security Deposit" value={formatAmount(adjustment.unused_security_deposit)} />
            <StatRow label="Partial Payments" value={formatAmount(adjustment.partial_payment_amount)} />
            <StatRow label="Other Adjustments" value={formatAmount(adjustment.loss_adjustment)} />
            <StatRow label="Months Lost" value={asNumberOrDash(adjustment.months_lost)} />
            <StatRow label="Lost Revenue" value={formatAmount(adjustment.lost_revenue)} />
          </div>
        </div>
      </div>
    </SubCard>
  )
}

function SubCardHeading({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <h4 className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
      <span className="text-[#1597ff]" aria-hidden="true">
        {icon}
      </span>
      {children}
    </h4>
  )
}

function StatRow({ label, value }: { label: string; value?: React.ReactNode }) {
  const display = value === null || value === undefined || value === '' ? '-' : value
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="shrink-0 text-slate-500">{label}</span>
      <span className="min-w-0 text-right font-semibold text-slate-900">{display}</span>
    </div>
  )
}

function asNumberOrDash(value: unknown): string {
  if (value === undefined || value === null || value === '') return '-'
  return String(value as string | number)
}
